import { useFormField } from "../../hooks/useFormField";
import { FieldHint } from "../FieldHint";
import { labelClass } from "./fieldStyles";
import type { IntakeFormData } from "../../types/form";
import type { useStepValidation } from "../../hooks/useStepValidation";

type StringField = {
  [K in keyof IntakeFormData]: IntakeFormData[K] extends string | null ? K : never;
}[keyof IntakeFormData];

interface ButtonGroupInputProps {
  field: StringField;
  label: string;
  options: string[];
  validation: ReturnType<typeof useStepValidation>;
  required?: boolean;
  containerClassName?: string;
  onChange?: (value: string) => void;
  /** Return false to prevent the value from being set. Use for confirmation guards. */
  onBeforeChange?: (value: string) => boolean;
  /** When true, suppresses prefill and mismatch hints — use on steps where fields are never proposal-prefilled. */
  noPrefill?: boolean;
  /** Static helper text rendered below the button row. */
  hint?: string;
}

/**
 * Radio-style button group for string fields. Renders one button per option
 * using the same active/inactive styling as ToggleInput. Integrates with the
 * step validation system for inline error display.
 *
 * Usage:
 *   <ButtonGroupInput field="portalReplaces" label="Who should this person replace?"
 *     options={["Company Contact", "Fiduciary"]} validation={validation} required />
 */
export function ButtonGroupInput({ field, label, options, validation, required, containerClassName, onChange, onBeforeChange, noPrefill, hint }: ButtonGroupInputProps) {
  const f = useFormField(field, validation);

  const base = "flex-1 py-2.5 text-sm font-medium rounded-lg border transition-colors cursor-pointer text-center focus:outline-none focus:ring-2 focus:ring-brand-green-50";
  const active = "bg-brand-green-10 text-brand-green border-brand-green font-semibold";
  const inactiveBase = f.error
    ? "bg-danger-bg text-gray-600 border-danger"
    : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50";

  return (
    <div className={containerClassName}>
      <span id={`${field}-label`} className={labelClass()}>
        {label}
        {required && <span className="text-danger" aria-hidden="true">&nbsp;*</span>}
      </span>
      {hint && <p className="text-xs text-gray-500 mb-1.5">{hint}</p>}
      <div
        className="flex gap-2"
        role="radiogroup"
        aria-labelledby={`${field}-label`}
        aria-invalid={f.error ? true : undefined}
        aria-describedby={f.error ? `${field}-hint` : undefined}
        onBlur={f.onBlur}
      >
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            role="radio"
            aria-checked={f.value === opt}
            className={`${base} ${f.value === opt ? active : inactiveBase}`}
            onClick={() => { if (onBeforeChange && !onBeforeChange(opt)) return; f.setValue(opt as never); f.onBlur(); onChange?.(opt); }}
          >
            {opt}
          </button>
        ))}
      </div>
      <FieldHint id={`${field}-hint`} error={f.error} isPrefilled={noPrefill ? false : f.isPrefilled} mismatch={noPrefill ? undefined : f.mismatch} />
    </div>
  );
}
